import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Divider,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  SERVICE_TYPES, 
  SERVICE_ROLES,
  serviceTypeLabels, 
  serviceRoleLabels 
} from '../constants/services';
import {
  getServices,
  createService,
  deleteService,
  updateService
} from '../services/serviceManagementService';
import * as yup from 'yup';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { firebaseConfig } from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';

const ITEMS_PER_PAGE = 10;

const schema = yup.object().shape({
  name: yup.string().required('Le nom est requis'),
  type: yup.string().required('Le type est requis'),
  role: yup.string().required('Le rôle est requis'),
  email: yup.string().email('Email invalide').required('L\'email est requis'),
  password: yup.string().required('Le mot de passe est requis').min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
});

const ServiceManagement = () => {
  const { userSchool } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // États du formulaire
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    customType: '', // Nouveau champ pour le type personnalisé
    role: SERVICE_ROLES.TECHNICAL,
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // États dialog édition email/mot de passe
  const [editService, setEditService] = useState(null);
  const [editForm, setEditForm] = useState({ newEmail: '', newPassword: '', confirmPassword: '' });
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editSuccess, setEditSuccess] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);

  // Chargement des services
  useEffect(() => {
    let isMounted = true;

    const loadServices = async () => {
      try {
        setError(null);
        setLoading(true);
        
        // Vérification si userSchool est défini
        if (!userSchool) {
          console.log('École non définie, impossible de charger les services');
          if (isMounted) {
            setServices([]);
            setTotalItems(0);
            setLoading(false);
          }
          return;
        }
        
        console.log('Chargement des services pour l\'école:', userSchool);
        
        try {
          const { services: servicesList, total } = await getServices(userSchool, page, ITEMS_PER_PAGE);
          
          if (isMounted) {
            console.log('Services chargés avec succès:', servicesList);
            setServices(servicesList || []);
            setTotalItems(total || 0);
            setLoading(false);
          }
        } catch (serviceError) {
          console.error('Erreur lors de la récupération des services:', serviceError);
          if (isMounted) {
            // En cas d'erreur, on affiche quand même la page avec un message d'erreur
            setServices([]);
            setError('Une erreur est survenue lors du chargement des services');
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Erreur générale:', error);
        if (isMounted) {
          setError('Une erreur inattendue est survenue');
          setServices([]);
          setLoading(false);
        }
      }
    };

    // Toujours tenter de charger les services, même si userSchool est undefined
    // Cela permettra d'afficher au moins la structure de la page
    loadServices();

    return () => {
      isMounted = false;
    };
  }, [userSchool, page]);

  const fetchServices = async () => {
    try {
      console.log('%c === DÉBUT RÉCUPÉRATION DES SERVICES ===', 'background: #ff0; color: #000; font-weight: bold;');
      setLoading(true);
      setError(null);
      
      if (!userSchool) {
        console.error('École non définie, impossible de rafraîchir les services');
        setServices([]);
        setLoading(false);
        return;
      }
      
      console.log('Rafraîchissement des services pour l\'école:', userSchool);
      console.log('ID de l\'école:', userSchool.id);
      console.log('Page actuelle:', page);
      console.log('Nombre d\'éléments par page:', ITEMS_PER_PAGE);
      
      const result = await getServices(userSchool.id, page, ITEMS_PER_PAGE);
      console.log('Résultat de getServices:', result);
      
      const newServices = result.services || [];
      const total = result.total || 0;
      
      console.log('Services récupérés:', newServices.length);
      console.log('Total des services:', total);
      
      setServices(newServices);
      setTotalItems(total);
      console.log('%c Services chargés avec succès', 'background: #0f0; color: #000; font-weight: bold;');
    } catch (error) {
      console.error('%c ERREUR lors du chargement des services:', 'background: #f00; color: #fff; font-weight: bold;', error);
      setError('Une erreur est survenue lors du chargement des services: ' + error.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Gestion du formulaire
  const validateForm = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      return {};
    } catch (error) {
      const errors = {};
      error.inner.forEach((err) => {
        errors[err.path] = err.message;
      });
      return errors;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log('Soumission du formulaire de création de service');
    
    const errors = await validateForm();
    
    // Validation supplémentaire pour le type personnalisé
    if (formData.type === SERVICE_TYPES.AUTRE && !formData.customType.trim()) {
      errors.customType = 'Veuillez préciser le type de service';
    }
    
    if (Object.keys(errors).length > 0) {
      console.log('Erreurs de validation du formulaire:', errors);
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      console.log('Les mots de passe ne correspondent pas');
      setFormErrors({
        ...formErrors,
        confirmPassword: 'Les mots de passe ne correspondent pas'
      });
      setLoading(false);
      return;
    }

    try {
      console.log('%c === DÉBUT CRÉATION SERVICE ===', 'background: #ff0; color: #000; font-weight: bold;');
      console.log('Tentative de création d\'utilisateur Firebase avec email:', formData.email);
      console.log('Mot de passe fourni (longueur):', formData.password?.length || 0);
      
      // Utiliser une app Firebase secondaire pour créer l'utilisateur
      // sans déconnecter l'admin de la session principale
      let secondaryApp = null;
      let uid;
      try {
        const secondaryAppName = 'secondary-user-creation-' + Date.now();
        secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
        const secondaryAuth = getAuth(secondaryApp);

        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          formData.email,
          formData.password
        );
        uid = userCredential.user.uid;
        await secondaryAuth.signOut();
      } catch (authError) {
        console.error('ERREUR création utilisateur:', authError.code, authError.message);
        throw authError;
      } finally {
        if (secondaryApp) {
          try { await deleteApp(secondaryApp); } catch (_) {}
        }
      }
      
      if (!uid) {
        console.error('UID non défini après création utilisateur');
        throw new Error('UID non défini après création utilisateur');
      }
      
      if (!userSchool?.id) throw new Error('École non définie');

      // Créer le document utilisateur (users/{uid})
      await setDoc(doc(db, COLLECTIONS.USERS, uid), {
        email: formData.email,
        displayName: formData.name,
        role: formData.role,
        serviceId: uid,
        active: true,
        createdAt: new Date().toISOString(),
        schoolId: userSchool.id,
      });

      // Créer le document service (services/{uid})
      await setDoc(doc(db, COLLECTIONS.SERVICES, uid), {
        id: uid,
        name: formData.name,
        type: formData.type === SERVICE_TYPES.AUTRE ? formData.customType : formData.type,
        role: formData.role,
        email: formData.email,
        schoolId: userSchool.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Rafraîchir la liste des services
      await fetchServices();
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        type: '',
        customType: '',
        role: SERVICE_ROLES.TECHNICAL,
        email: '',
        password: '',
        confirmPassword: '',
      });
      
      setOpenDialog(false);
    } catch (error) {
      console.error('Erreur lors de la création du service:', error);
      setFormErrors({
        ...formErrors,
        submit: 'Erreur lors de la création du service: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteService(serviceId, userSchool.id);
      await fetchServices();
    } catch (error) {
      console.error('Erreur lors de la suppression du service:', error);
      setError('Une erreur est survenue lors de la suppression du service');
    } finally {
      setLoading(false);
    }
  };

  function openEditDialog(service) {
    setEditService(service);
    setEditForm({ newEmail: '', newPassword: '', confirmPassword: '' });
    setEditErrors({});
    setEditSuccess('');
  }

  async function handleEditSubmit() {
    const errs = {};
    const changingEmail    = editForm.newEmail.trim() !== '';
    const changingPassword = editForm.newPassword !== '';

    if (!changingEmail && !changingPassword) {
      errs.general = 'Renseignez au moins un nouvel email ou un nouveau mot de passe.';
    }
    if (changingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.newEmail.trim())) {
      errs.newEmail = 'Email invalide.';
    }
    if (changingPassword && editForm.newPassword.length < 6) {
      errs.newPassword = 'Le mot de passe doit contenir au moins 6 caractères.';
    }
    if (changingPassword && editForm.newPassword !== editForm.confirmPassword) {
      errs.confirmPassword = 'Les mots de passe ne correspondent pas.';
    }
    if (Object.keys(errs).length > 0) { setEditErrors(errs); return; }

    setEditLoading(true);
    setEditErrors({});
    setEditSuccess('');
    try {
      // Appel Cloud Function — Admin SDK, aucun email envoyé
      const fn = httpsCallable(getFunctions(), 'updateServiceAccount');
      await fn({
        uid:         editService.id,
        newEmail:    changingEmail    ? editForm.newEmail.trim() : undefined,
        newPassword: changingPassword ? editForm.newPassword    : undefined,
      });

      setEditSuccess('Identifiants mis à jour sans envoi d\'email.');
      if (changingEmail) {
        setEditService(prev => ({ ...prev, email: editForm.newEmail.trim() }));
      }
      await fetchServices();
    } catch (err) {
      console.error('handleEditSubmit:', err);
      if (err.code === 'auth/email-already-in-use') {
        setEditErrors({ newEmail: 'Cet email est déjà utilisé.' });
      } else {
        setEditErrors({ general: err.message || 'Erreur lors de la mise à jour.' });
      }
    } finally {
      setEditLoading(false);
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Gestion des Services</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          disabled={loading || !userSchool}
        >
          Ajouter un service
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : !userSchool ? (
        <Alert severity="info" sx={{ my: 2 }}>Veuillez sélectionner une école pour gérer les services</Alert>
      ) : services.length === 0 ? (
        <Typography sx={{ my: 2 }}>Aucun service trouvé</Typography>
      ) : (
        <>
          <List>
            {services.map((service) => (
              <React.Fragment key={service.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Modifier email / mot de passe">
                        <IconButton size="small" onClick={() => openEditDialog(service)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton edge="end" size="small" onClick={() => handleDelete(service.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1">{service.name}</Typography>
                      <Box>
                        {service.type && (
                          <Chip 
                            label={serviceTypeLabels[service.type] || service.type} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                        )}
                        <Chip 
                          label={service.role === SERVICE_ROLES.TECHNICAL ? 'Technique' : 'Demandeur'} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {service.email}
                    </Typography>
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>

          {totalItems > ITEMS_PER_PAGE && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={Math.ceil(totalItems / ITEMS_PER_PAGE)}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Nouveau Service</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nom du service"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <FormControl fullWidth margin="dense" error={!!formErrors.type}>
              <InputLabel>Type de service</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => {
                  setFormData({ ...formData, type: e.target.value, customType: '' });
                  // Réinitialiser l'erreur du type personnalisé si on change de type
                  if (e.target.value !== SERVICE_TYPES.AUTRE) {
                    setFormErrors({ ...formErrors, customType: '' });
                  }
                }}
                label="Type de service"
              >
                {Object.entries(SERVICE_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {serviceTypeLabels[value]}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.type && <FormHelperText>{formErrors.type}</FormHelperText>}
            </FormControl>

            {/* Champ personnalisé pour "Autre" */}
            {formData.type === SERVICE_TYPES.AUTRE && (
              <TextField
                margin="dense"
                label="Précisez le type de service"
                fullWidth
                value={formData.customType}
                onChange={(e) => setFormData({ ...formData, customType: e.target.value })}
                error={!!formErrors.customType}
                helperText={formErrors.customType || "Veuillez préciser le type de service"}
                placeholder="Ex: Reprographie, Restauration, etc."
                required
              />
            )}

            <FormControl fullWidth margin="dense" error={!!formErrors.role}>
              <InputLabel>Rôle du service</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Rôle du service"
              >
                <MenuItem value={SERVICE_ROLES.TECHNICAL}>Service technique</MenuItem>
                <MenuItem value={SERVICE_ROLES.REQUESTER}>Service demandeur</MenuItem>
              </Select>
              {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
            </FormControl>

            <TextField
              margin="dense"
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="dense"
              label="Confirmer le mot de passe"
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {formErrors.submit && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formErrors.submit}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* ── Dialog modification email / mot de passe ── */}
      <Dialog open={Boolean(editService)} onClose={() => !editLoading && setEditService(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier les identifiants — {editService?.name}</DialogTitle>
        <DialogContent dividers>
          {editErrors.general && <Alert severity="error" sx={{ mb: 2 }}>{editErrors.general}</Alert>}
          {editSuccess && <Alert severity="success" sx={{ mb: 2 }}>{editSuccess}</Alert>}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Email actuel : <strong>{editService?.email}</strong>
          </Typography>

          <TextField
            fullWidth margin="dense" label="Nouvel email (optionnel)"
            type="email" value={editForm.newEmail}
            onChange={e => setEditForm(p => ({ ...p, newEmail: e.target.value }))}
            error={!!editErrors.newEmail} helperText={editErrors.newEmail}
            disabled={editLoading}
          />

          <TextField
            fullWidth margin="dense" label="Nouveau mot de passe (optionnel)"
            type={showEditPassword ? 'text' : 'password'} value={editForm.newPassword}
            onChange={e => setEditForm(p => ({ ...p, newPassword: e.target.value }))}
            error={!!editErrors.newPassword} helperText={editErrors.newPassword || 'Minimum 6 caractères'}
            disabled={editLoading}
            InputProps={{ endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowEditPassword(v => !v)}>
                  {showEditPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )}}
          />

          {editForm.newPassword && (
            <TextField
              fullWidth margin="dense" label="Confirmer le nouveau mot de passe"
              type="password" value={editForm.confirmPassword}
              onChange={e => setEditForm(p => ({ ...p, confirmPassword: e.target.value }))}
              error={!!editErrors.confirmPassword} helperText={editErrors.confirmPassword}
              disabled={editLoading}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setEditService(null)} disabled={editLoading}>Annuler</Button>
          <Button variant="contained" onClick={handleEditSubmit} disabled={editLoading}>
            {editLoading ? <CircularProgress size={20} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ServiceManagement;
